import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import PlaceManagementDialog from "@/components/PlaceManagementDialog";
import { Settings } from "lucide-react";

export function AddCustomerInline({ open, onOpenChange, defaultName, onAdded, custQ }) {
  const [form, setForm] = useState({
    name: "",
    place: "",
    phone1: "",
    phone2: "",
  });
  const [saving, setSaving] = useState(false);
  const [placeDialogOpen, setPlaceDialogOpen] = useState(false);
  
  // Refs for Enter key navigation
  const nameRef = useRef(null);
  const phone1Ref = useRef(null);
  const phone2Ref = useRef(null);

  useEffect(() => {
    if (open) {
      // Pre-fill phone if custQ is a 10-digit phone number
      const phone = /^\d{10}$/.test(custQ) ? custQ : "";
      setForm({
        name: defaultName || "",
        place: "",
        phone1: phone || "",
        phone2: "",
      });
    }
  }, [open, defaultName, custQ]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        place: form.place.trim(),
        phone1: form.phone1.trim(),
        phone2: form.phone2.trim(),
      };
      const { data } = await api.post("/customers", payload);
      toast.success(`Added ${data.name}`);
      onAdded?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="inline-customer-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">New customer</DialogTitle>
            <DialogDescription>Add a customer on the fly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</Label>
              <Input
                ref={nameRef}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // Focus Place autocomplete input
                    const placeInput = document.querySelector('[data-testid="place-autocomplete-input"]');
                    placeInput?.focus();
                  }
                }}
                className="mt-1"
                data-testid="inline-customer-name"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Place</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setPlaceDialogOpen(true)}
                  title="Manage places"
                  data-testid="inline-manage-places-btn"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Manage
                </Button>
              </div>
              <div className="mt-1">
                <PlaceAutocomplete
                  value={form.place}
                  onChange={(val) => setForm({ ...form, place: val })}
                  placeholder="Type 2+ chars to search places..."
                  onEnterToNext={() => phone1Ref.current?.focus()}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone 1</Label>
                <Input
                  ref={phone1Ref}
                  value={form.phone1}
                  onChange={(e) => setForm({ ...form, phone1: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      phone2Ref.current?.focus();
                    }
                  }}
                  className="mt-1 font-mono-data"
                  data-testid="inline-customer-phone1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone 2</Label>
                <Input
                  ref={phone2Ref}
                  value={form.phone2}
                  onChange={(e) => setForm({ ...form, phone2: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  className="mt-1 font-mono-data"
                  data-testid="inline-customer-phone2"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving || !form.name.trim()}
              style={{ backgroundColor: "#9E6D5B" }}
            >
              {saving ? "Saving…" : "Add customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Place Management Dialog */}
      <PlaceManagementDialog
        open={placeDialogOpen}
        onOpenChange={setPlaceDialogOpen}
      />
    </>
  );
}
